import { id } from "../../utils";
import type { Item, ItemsState, ParameterDefinition, StringState } from "../ReportServices";

type SaveReportParamsOptions = {
  reportName: string;
  parameterDefinitions: ParameterDefinition[];
  dataDefinitions: ParameterDefinition[];
  selectedDataDefinitionIndex: number;
  selectedItem: StringState;
  selectedUdfs: string;
  items: ItemsState;
  udfs: Item[];
  dataLoadDuration: number;
  saveHeader?: boolean;
  setError: (value: string) => void;
};

const getExcelErrorMessage = (error: unknown): string => {
  const officeError = error as OfficeExtension.Error & { debugInfo?: { errorLocation?: string } };
  const parts = [officeError?.code, officeError?.message || String(error)].filter(Boolean);
  const location = officeError?.debugInfo?.errorLocation;
  return location ? `${parts.join(": ")} at ${location}` : parts.join(": ");
};

const buildParameterValues = ({
  parameterDefinitions,
  dataDefinitions,
  selectedDataDefinitionIndex,
  selectedItem,
  selectedUdfs,
  items,
  udfs,
}: Pick<SaveReportParamsOptions,
  "parameterDefinitions" |
  "dataDefinitions" |
  "selectedDataDefinitionIndex" |
  "selectedItem" |
  "selectedUdfs" |
  "items" |
  "udfs"
>) => {
  const parameterValues = parameterDefinitions.map((parameter) => {
    const nameId = id(parameter.name);
    const value = selectedItem[nameId] || "";
    const text =
      (parameter.type.startsWith("select") || parameter.type.startsWith("multiselect")
        ? parameter.type.startsWith("multiselect")
          ? selectedItem[nameId]
              ?.split(", ")
              ?.map((itemId) => items[nameId]?.find((item) => item.id === itemId)?.name)
              .join(", ")
          : items[nameId]?.find((item) => item.id === selectedItem[nameId])?.name
        : selectedItem[nameId]?.toString()) || "";
    return { name: parameter.name, value, text: text || "N/A" };
  });

  parameterValues.push({
    name: "Data",
    value: dataDefinitions[selectedDataDefinitionIndex].name,
    text: dataDefinitions[selectedDataDefinitionIndex].name,
  });

  if (selectedUdfs?.length ?? 0 > 0) {
    parameterValues.push({
      name: "UDFs",
      value: selectedUdfs,
      text: selectedUdfs.split(", ").map((itemId) => udfs.find((item) => item.id === itemId)?.name || itemId).join(", "),
    });
  }

  return parameterValues;
};

export const saveReportParams = async (context: Excel.RequestContext, options: SaveReportParamsOptions) => {
  const {
    reportName,
    selectedItem,
    dataLoadDuration,
    saveHeader = false,
    setError,
  } = options;

  let saveStep = "opening parameters sheet";
  let sheet: Excel.Worksheet | null = null;

  try {
    sheet = context.workbook.worksheets.getItemOrNullObject("(parameters)");
    await context.sync();
    if (sheet.isNullObject) {
      saveStep = "creating parameters sheet";
      sheet = context.workbook.worksheets.add("(parameters)");
      await context.sync();
    } else {
      saveStep = "making parameters sheet writable";
      sheet.load("visibility");
      await context.sync();

      if (sheet.visibility !== "Visible") {
        sheet.visibility = "Visible";
        await context.sync();
      }

      try {
        sheet.protection.unprotect();
        await context.sync();
      } catch {
        saveStep = "recreating locked parameters sheet";
        sheet.delete();
        await context.sync();

        sheet = context.workbook.worksheets.add("(parameters)");
        await context.sync();
      }

      saveStep = "clearing parameters sheet";
      const usedRange = sheet.getUsedRangeOrNullObject();
      await context.sync();
      if (!usedRange.isNullObject) {
        usedRange.clear();
        await context.sync();
      }
    }

    saveStep = "writing report metadata";
    let range = sheet.getRange("A1:B1");
    range.values = [["Analyzer:", reportName || ""]];
    await context.sync();
    if (reportName?.length > 0) {
      saveStep = "updating report named range";
      const reportNameRange = context.workbook.names.getItemOrNullObject("report");
      await context.sync();
      if (!reportNameRange.isNullObject) {
        reportNameRange.delete();
      }
      range = sheet.getRange("B1:B1");
      context.workbook.names.add("report", range);
    }

    const parameterValues = buildParameterValues(options);
    if (parameterValues.length > 0) {
      saveStep = "writing parameter summary";
      sheet.getRange("A2:B2").values = [
        ["Parameters:", saveHeader ? parameterValues.map((parameter) => parameter.name + ": " + parameter.text).join(", ") : "..."],
      ];
      await context.sync();

      saveStep = "updating parameters named range";
      const parametersNameRange = context.workbook.names.getItemOrNullObject("parameters");
      await context.sync();
      if (!parametersNameRange.isNullObject) {
        parametersNameRange.delete();
      }
      range = sheet.getRange("B2:B2");
      context.workbook.names.add("parameters", range);
      await context.sync();

      saveStep = "writing parameter values";
      range = sheet.getRange(`A3:C${2 + parameterValues.length}`);
      range.numberFormat = Array(parameterValues.length).fill(Array(3).fill("@"));
      range.values = parameterValues.map((parameterValue) => [parameterValue.name, parameterValue.value, parameterValue.text]);
      await context.sync();

      saveStep = "writing run time";
      const lastDataLoad = new Date().toLocaleString();
      sheet.getRange(`A${3 + parameterValues.length}:B${3 + parameterValues.length}`).values = [["Run at:", lastDataLoad]];
      await context.sync();

      saveStep = "writing processing duration";
      sheet.getRange(`A${4 + parameterValues.length}:B${4 + parameterValues.length}`).values = [["Processing:", dataLoadDuration.toFixed(2) + " seconds"]];
      await context.sync();
    }

    saveStep = "updating parameters table named range";
    const parametersTableNameRange = context.workbook.names.getItemOrNullObject("parameters_table");
    await context.sync();
    if (!parametersTableNameRange.isNullObject) {
      parametersTableNameRange.delete();
    }

    const usedRange = sheet.getUsedRange();
    context.workbook.names.add("parameters_table", usedRange);
    await context.sync();

    if (saveHeader) {
      saveStep = "protecting parameters sheet";
      sheet.protection.protect();
      await context.sync();
    }

    saveStep = "hiding parameters sheet";
    sheet.visibility = "Hidden";
    await context.sync();
  } catch (error) {
    setError(`Save params failed while ${saveStep}: ${getExcelErrorMessage(error)}`);

    try {
      if (sheet && !sheet.isNullObject) {
        sheet.visibility = "Hidden";
        await context.sync();
      }
    } catch {
      // Keep the original Excel error visible to the user.
    }
  }
};
